<template>
    <div class="p-5 h-full dark:bg-gray-800 dark:text-white flex">
        <div class="flex flex-col w-full justify-stretch">
            <!-- navigation header -->
            <div class="flex justify-between mb-5 shrink-0 grow-0 gap-4">
                <div class="flex gap-2 items-center">
                    <span
                        class="font-bold text-lg leading-none text-gray-500 dark:text-gray-200 mr-3 hidden lg:inline-block"
                        >{{ $t('convert_heading_start') }}</span
                    >
                    <span
                        class="font-bold text-lg leading-none tracking-wide rounded-[3px] bg-[#5560A9] text-white px-2 py-1"
                        >HEIC</span
                    >
                    <span
                        class="font-bold text-lg leading-none text-gray-500 dark:text-gray-200"
                        ><i-tabler-arrow-narrow-right
                            class="w-7 h-7"></i-tabler-arrow-narrow-right
                    ></span>
                    <format-dropdown
                        format="jpg"
                        @update="onFormatSelect"></format-dropdown>
                </div>
                <div class="flex items-center">
                    <button
                        type="button"
                        :disabled="zipDownloadGettingReady"
                        class="bg-[#ffe797] hover:bg-[#ffe07c] focus:ring-4 focus:outline-none font-medium rounded-lg text-md px-5 py-2.5 text-center inline-flex items-center gap-2 me-4 text-gray-700 hover:text-gray-900 cursor-pointer"
                        :class="{ invisible: !canDownloadAll }"
                        @click="onDownloadAllAsZipBtnClick"
                        data-tooltip-target="tooltip-download-all-zip"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-download
                            v-if="!zipDownloadGettingReady"
                            class="w-[24px] h-[24px]"></i-tabler-download>
                        <i-tabler-loader-2
                            v-if="zipDownloadGettingReady"
                            class="w-[24px] h-[24px] animate-spin"></i-tabler-loader-2>
                        ZIP
                    </button>

                    <button
                        type="button"
                        class="bg-gray-200 hover:bg-orange-200 focus:ring-4 focus:outline-none font-medium rounded-lg text-md px-5 py-2.5 text-center inline-flex items-center gap-2 me-12 text-gray-700 hover:text-gray-900 cursor-pointer"
                        :class="{ invisible: !canDownloadAll }"
                        @click="onDownloadAllBtnClick"
                        data-tooltip-target="tooltip-download-all"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-download
                            v-if="!downloadAllGettingReady"
                            class="w-[24px] h-[24px]"></i-tabler-download>
                        <i-tabler-loader-2
                            v-if="downloadAllGettingReady"
                            class="w-[24px] h-[24px] animate-spin"></i-tabler-loader-2>
                        <span class="hidden lg:inline-block">{{
                            $t('download_all_label')
                        }}</span>
                    </button>

                    <label
                        v-if="files.items.length > 0"
                        for="dropzone-file-top"
                        class="text-white bg-gradient-to-br from-pink-500 to-orange-400 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-pink-200 dark:focus:ring-pink-800 font-medium rounded-lg text-md px-5 py-2.5 text-center inline-flex items-center gap-2 cursor-pointer"
                        data-tooltip-target="tooltip-upload"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-cloud-upload
                            class="text-orange-100 h-5 w-5 tabler-stroke-1-5 shrink-0"></i-tabler-cloud-upload>
                        <span class="hidden lg:inline-block">{{
                            $t('upload_heic_files_label')
                        }}</span>
                        <input
                            ref="dropzoneFileTop"
                            id="dropzone-file-top"
                            type="file"
                            accept=".heic, image/heic"
                            multiple
                            class="hidden"
                            @change="onFileInputChange($event, 'top')" />
                    </label>
                </div>
                <div class="flex gap-3 items-center grow-0">
                    <language-dropdown
                        class="mr-7"
                        :lang="lang"
                        @update="onLangUpdate"></language-dropdown>
                    <!-- help button -->
                    <a
                        :href="getWebsiteUrl('/how-to/')"
                        class="text-sm w-8 h-8 inline-flex justify-center items-center dark:text-[#6e7bcf]- dark:hover:text-[#8391eb] dark:text-gray-400 text-[#5560A9]- text-gray-400 hover:text-[#363f7b]"
                        @click.prevent="onHowToClick"
                        data-tooltip-target="tooltip-how-to"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-help-circle
                            class="w-8 h-8"></i-tabler-help-circle>
                    </a>
                </div>
            </div>

            <!-- drop zone displayed by default -->
            <div
                v-if="files.items.length == 0"
                for="dropzone-file"
                ref="dropzoneElm"
                class="grow transition-all rounded-lg cursor-pointer relative flex flex-col gap-4 items-center justify-center w-full border-2 border-gray-300 border-dashed bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                @drop="onDropZoneDrop">
                <div
                    v-if="files.items.length == 0"
                    class="flex flex-col gap-4 items-center justify-center px-5">
                    <i-tabler-cloud-upload
                        class="text-gray-500 h-10 w-10"></i-tabler-cloud-upload>
                    <p
                        class="mb-2 text-lg text-gray-500 dark:text-gray-400 text-center">
                        {{ t('click_to_upload_message') }}
                    </p>
                </div>
                <label
                    for="dropzone-file"
                    class="absolute top-0 left-0 bottom-0 right-0 opacity-0 cursor-pointer">
                    <input
                        ref="dropzoneFile"
                        id="dropzone-file"
                        type="file"
                        accept=".heic, image/heic"
                        multiple
                        class="hidden"
                        @change="onFileInputChange($event, 'middle')" />
                </label>
            </div>

            <!-- files being converted -->
            <div class="grow overflow-auto mt-3" v-if="files.items.length > 0">
                <div class="flex flex-wrap gap-4">
                    <div
                        v-for="(file, index) in files.items"
                        :key="index"
                        class="border-[1px] border-gray-300 dark:border-gray-500 shadow-md rounded-md group/item cursor-pointer flex flex-col items-stretch justify-between"
                        @click="onItemClick($event, index)">
                        <!-- img container -->
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-center justify-center gap-2"
                            v-if="downloadItemGettingReady === index">
                            <file-processing-spinner
                                v-if="
                                    downloadItemGettingReady === index
                                "></file-processing-spinner>
                            <span class="text-sm">{{
                                $t('downloading_status_message')
                            }}</span>
                        </div>
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-center justify-center gap-2"
                            v-if="file.processing">
                            <file-processing-spinner
                                v-if="
                                    file.processing
                                "></file-processing-spinner>
                            <span class="text-sm">{{
                                $t('converting_status_message')
                            }}</span>
                        </div>
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-center justify-center gap-2"
                            v-if="
                                !file.processing &&
                                file.error &&
                                downloadItemGettingReady !== index
                            ">
                            <span
                                class="text-sm px-4 text-center"
                                v-if="file.detectedFormat"
                                >{{ $t('detected_format_message', {format:
                                `<span class="uppercase font-bold"
                                    >${file.detectedFormat}</span
                                >`}) }}
                                <span class="underline hover:no-underline">{{
                                    $t('rename_and_download_label')
                                }}</span>
                            </span>
                            <span
                                class="text-sm text-red-600 dark:text-red-400 px-4 text-center"
                                v-if="!file.detectedFormat && file.error"
                                >{{ file.error }}</span
                            >
                            <div
                                class="rounded-full w-8 h-8 bg-gray-100 right-2 top-12 flex flex-col items-center justify-center cursor-pointer"
                                @click="onDeleteBtnClick($event, index)">
                                <i-tabler-trash
                                    class="w-5 h-5 text-gray-700 hover:text-red-600"></i-tabler-trash>
                            </div>
                        </div>
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-start"
                            v-if="
                                !file.processing &&
                                !file.error &&
                                downloadItemGettingReady !== index
                            ">
                            <img
                                :src="file.src"
                                class="absolute- inset-0- object-cover w-full h-full group-hover/item:opacity-50-"
                                alt="" />
                            <!-- del icon -->
                            <div
                                class="absolute rounded-full w-8 h-8 bg-white right-2 top-12 hidden group-hover/item:flex flex-col items-center justify-center cursor-pointer group/icon item-icon"
                                @click="onDeleteBtnClick($event, index)">
                                <i-tabler-trash
                                    class="w-5 h-5 text-gray-700 group-hover/icon:text-red-600"></i-tabler-trash>
                            </div>
                            <!-- maximize icon -->
                            <div
                                class="absolute rounded-full w-8 h-8 bg-white right-2 top-2 hidden group-hover/item:flex flex-col items-center justify-center cursor-pointer group/icon item-icon"
                                @click="onMaximizeBtnClick($event, index)">
                                <i-tabler-maximize
                                    class="w-5 h-5 text-gray-700 group-hover/icon:text-red-600"></i-tabler-maximize>
                            </div>
                        </div>
                        <!-- item name -->
                        <div
                            class="text-center text-gray-700 dark:text-gray-200 text-sm break-all p-2 w-32 font-bold group-hover/item:text-gray-300- dark:group-hover/item:text-gray-500-"
                            :title="
                                file.name !== file.shortName ? file.name : ''
                            ">
                            {{ file.shortName }}
                        </div>
                        <!-- download link -->
                        <div
                            v-if="
                                !file.processing &&
                                !file.error &&
                                downloadItemGettingReady !== index
                            "
                            class="text-sm text-center right-0 bg-[#CB5FA0] hover:bg-[#bf4c91] text-white font-medium py-2 underline rounded-b-md hover:no-underline">
                            {{
                                $t('download_label', {
                                    format: formats[file.format as FormatID]
                                        .title,
                                })
                            }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- footer -->
            <div
                class="text-sm mt-5 text-gray-500 dark:text-gray-400 text-center">
                {{ $t('support_email_label') }}
                <a
                    class="underline hover:no-underline"
                    :href="`mailto:${getSupportEmail()}`"
                    >{{ getSupportEmail() }}</a
                >
            </div>

            <!-- fullscreen drop zone: it appears only when user drags a file to the screen -->
            <div
                ref="fullscreenDropZone"
                v-if="isFullscreenDropZoneVisible"
                @dragleave="onFullscreenDropzoneDragLeave"
                class="absolute z-[999999] top-0 left-0 bottom-0 right-0 bg-gray-700/40">
                <div
                    class="absolute top-4 left-4 bottom-4 right-4 border-dashed border-4 border-white">
                    <p class="text-center text-6xl text-white font-medium mt-5">
                        {{ $t('upload_heic_files_dropzone_label') }}
                    </p>
                </div>
                <input
                    ref="dropzoneFile"
                    id="dropzone-file"
                    type="file"
                    accept=".heic, image/heic"
                    multiple
                    class="absolute top-0 left-0 bottom-0 right-0 opacity-0 cursor-pointer"
                    @change="onFileInputChange($event, 'fullscreen')" />
            </div>

            <!-- tooltips -->
            <tooltip-bubble id="tooltip-how-to">{{
                $t('how_to_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-download-all-zip">{{
                $t('download_all_as_zip_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-download-all">{{
                $t('download_all_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-upload" class="block lg:hidden">{{
                $t('upload_heic_files_label')
            }}</tooltip-bubble>

            <!-- modal -->
            <div
                id="item-modal"
                ref="modalElm"
                tabindex="-1"
                aria-hidden="true"
                class="hidden overflow-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%-1rem)]- bottom-0 backdrop-blur-md">
                <div
                    class="relative p-4 w-full max-w-3xl h-full overflow-hidden">
                    <!-- Modal content -->
                    <div
                        class="relative bg-white- rounded-sm shadow- dark:bg-gray-700 h-full overflow-hidden">
                        <!-- Modal header -->
                        <button
                            type="button"
                            class="fixed right-8 top-8 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center shadow cursor-pointer"
                            @click="modal.hide()">
                            <i-tabler-x class="w-6 h-6"></i-tabler-x>
                        </button>
                        <!-- Modal body -->
                        <div class="rounded-sm max-h-full overflow-auto shadow">
                            <img
                                v-if="activeItemIndex >= 0"
                                :src="files.items[activeItemIndex].src"
                                alt=""
                                class="" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Modal } from 'flowbite';
import type { ModalOptions, ModalInterface } from 'flowbite';
import type { InstanceOptions } from 'flowbite';
import { initTooltips } from 'flowbite';
import { downloadZip } from 'client-zip';
import {
    dataURItoBlob,
    getWebsiteUrl,
    getSupportEmail,
    isDevelopment,
    getShortFileName,
} from '~/helpers/utils';
import {
    ConvertHeicResultMessage,
    FormatID,
    LanguageCode,
} from '~/helpers/types';
import { formats } from '~/helpers/data';
import { useI18n } from 'vue-i18n';
import { detectLangOnPageLoad, setI18nLanguage } from '~/helpers/i18n';

const { t } = useI18n();

interface UploadedFile {
    id: string;
    name: string;
    shortName: string;
    src: string;
    processing: boolean;
    error: string;
    blob: any;
    detectedFormat?: string;
    format?: string;
}

const dropzoneFile = ref<HTMLInputElement | null>(null);
const dropzoneElm = ref<HTMLElement | null>(null);
const dropzoneFileTop = ref<HTMLInputElement | null>(null);
const files = reactive({
    items: [] as UploadedFile[],
});
const modalElm = ref<HTMLElement | null>(null);
let modal: ModalInterface;
const activeItemIndex = ref(-1);
const isFullscreenDropZoneVisible = ref(false);
const zipDownloadGettingReady = ref(false);
const downloadAllGettingReady = ref(false);
const downloadItemGettingReady = ref(-1);
const selectedFormat = ref<FormatID>('jpg');
const lang = ref<LanguageCode>('en');

const modalOptions: ModalOptions = {
    closable: true,
    onHide: () => {
        activeItemIndex.value = -1;
    },
    onShow: () => {
        // console.log('modal is shown');
    },
    onToggle: () => {
        // console.log('modal has been toggled');
    },
};

const instanceOptions: InstanceOptions = {
    id: 'modalEl',
    override: true,
};

const uniqueId = () => {
    return '_' + Math.random().toString(36).substring(2, 9);
};

const onFormatSelect = (format: FormatID) => {
    selectedFormat.value = format;
};

// Send message to sandbox to convert HEIC image to JPG
const sendHeicImageToConvert = async (itemIndex: number, file: any) => {
    const quality = 90; // Default quality for web version

    const message = {
        message: 'convert_heic',
        itemId: files.items[itemIndex].id,
        file: file,
        format: selectedFormat.value,
        quality: quality,
    };

    if (isDevelopment()) {
        console.log('[DEV] Sent request to convert image');
    }

    const iframe = document.getElementById('sandboxFrame') as HTMLIFrameElement;
    iframe?.contentWindow?.postMessage(message, '*');
};

const handleFiles = async (uploaded: FileList) => {
    for (let i = 0; i < uploaded.length; i++) {
        const file = uploaded[i];

        if (/\.heic$/i.test(file.name)) {
            try {
                let fileName = file.name;

                const index = files.items.findIndex(
                    (item) => item.name == fileName
                );
                if (index >= 0) {
                    let counter = 1;
                    while (
                        files.items.findIndex(
                            (item) =>
                                item.name ==
                                fileName.replace(
                                    /^(.*?)(\.heic)$/i,
                                    `$1-${counter}$2`
                                )
                        ) >= 0
                    ) {
                        counter++;
                    }
                    fileName = fileName.replace(
                        /^(.*?)(\.heic)$/i,
                        `$1-${counter}$2`
                    );
                }

                const item: UploadedFile = {
                    id: uniqueId(),
                    name: fileName,
                    shortName: getShortFileName(fileName),
                    src: '',
                    processing: true,
                    error: '',
                    blob: null,
                    format: selectedFormat.value,
                };

                files.items.push(item);
                const itemIndex = files.items.length - 1;

                await sendHeicImageToConvert(itemIndex, file);
            } catch (error) {
                console.error('Error handling HEIC file:', error);
            }
        }
    }
};

const onFileInputChange = (event: Event, source: string) => {
    const target = event.target as HTMLInputElement;
    if (target.files) {
        handleFiles(target.files);
    }
    target.value = '';
};

const onDropZoneDrop = (event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer?.files) {
        handleFiles(event.dataTransfer.files);
    }
};

const onWindowDragEnter = (event: DragEvent) => {
    event.preventDefault();
    isFullscreenDropZoneVisible.value = true;
};

const onFullscreenDropzoneDragLeave = (event: DragEvent) => {
    event.preventDefault();
    isFullscreenDropZoneVisible.value = false;
};

const onWindowMessage = (event: MessageEvent) => {
    const msg = event.data as ConvertHeicResultMessage;
    if (msg.message === 'convert_heic_result') {
        const index = files.items.findIndex((item) => item.id == msg.itemId);
        if (index >= 0) {
            files.items[index].processing = false;
            files.items[index].format = msg.format;
            if (msg.error) {
                if (isDevelopment()) {
                    console.log('HEIC convert error:', msg.errorObject);
                }
                // is this another format?
                const formats = [
                    'image/png',
                    'image/jpeg',
                    'image/gif',
                    'image/webp',
                ];
                for (let i = 0; i < formats.length; i++) {
                    if (msg.errorObject?.message.includes(formats[i])) {
                        files.items[index].error = t(
                            'unsupported_format_error'
                        );
                        files.items[index].detectedFormat = formats[i]
                            .replace('image/', '')
                            .replace('jpeg', 'jpg');
                        // in this case, this is the original file blob
                        files.items[index].src = msg.url;
                        return;
                    }
                }

                files.items[index].error = t('invalid_image_error');
            } else {
                files.items[index].src = msg.url;
            }
        }
        if (isDevelopment()) {
            console.log('[DEV] Conversion results', files.items);
        }
    }
};

const onItemClick = (event: Event, index: number) => {
    const item = files.items[index];
    if (!item.processing && !item.error) {
        downloadItemGettingReady.value = index;

        const link = document.createElement('a');
        link.href = item.src;
        link.download = item.name.replace(/\.heic$/i, `.${item.format}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        downloadItemGettingReady.value = -1;
    }
};

const onDeleteBtnClick = (event: Event, index: number) => {
    event.stopPropagation();
    files.items.splice(index, 1);
};

const onMaximizeBtnClick = (event: Event, index: number) => {
    event.stopPropagation();
    activeItemIndex.value = index;
    modal.show();
};

const onDownloadAllBtnClick = async () => {
    downloadAllGettingReady.value = true;

    for (let i = 0; i < files.items.length; i++) {
        const item = files.items[i];
        if (!item.processing && !item.error) {
            downloadItemGettingReady.value = i;

            const link = document.createElement('a');
            link.href = item.src;
            link.download = item.name.replace(/\.heic$/i, `.${item.format}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Small delay between downloads
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    downloadItemGettingReady.value = -1;
    downloadAllGettingReady.value = false;
};

const onDownloadAllAsZipBtnClick = async () => {
    zipDownloadGettingReady.value = true;

    const filesToZip = files.items
        .filter((item) => !item.processing && !item.error)
        .map((item) => ({
            name: item.name.replace(/\.heic$/i, `.${item.format}`),
            input: dataURItoBlob(item.src),
        }));

    if (filesToZip.length > 0) {
        const blob = await downloadZip(filesToZip).blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'heic-converted-images.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    zipDownloadGettingReady.value = false;
};

const onLangUpdate = (newLang: LanguageCode) => {
    lang.value = newLang;
    setI18nLanguage(newLang);
};

const onHowToClick = () => {
    window.open(getWebsiteUrl('/how-to/'), '_blank');
};

const canDownloadAll = computed(() => {
    return files.items.some((item) => !item.processing && !item.error);
});

onMounted(async () => {
    let langDetected = await detectLangOnPageLoad();

    if (langDetected) {
        if (isDevelopment()) {
            console.log('[DEV] lang detected', langDetected);
        }
        lang.value = langDetected as LanguageCode;
        setI18nLanguage(langDetected);
    }

    // init event handlers
    document.addEventListener('dragenter', onWindowDragEnter, false);
    window.addEventListener('message', onWindowMessage, false);

    // init modal
    modal = new Modal(modalElm.value, modalOptions, instanceOptions);

    // init flowbite tooltips
    await nextTick();
    initTooltips();
});

onUnmounted(() => {
    document.removeEventListener('dragenter', onWindowDragEnter, false);
    window.removeEventListener('message', onWindowMessage, false);
});
</script>

<style lang="scss" scoped>
.item-icon {
    box-shadow: 0 0 4px 1px rgba(57, 76, 96, 0.15),
        0 0 0 1px rgba(43, 59, 74, 0.3);
    transition: color 0.1s linear, background-color 0.1s linear;
}
</style>
